import { useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap, Flame, BadgeCheck, HeartHandshake, Users, CalendarClock,
  Sparkles, ArrowRight, ChevronDown, BookOpen, IndianRupee,
  Upload, CheckCircle2, Loader2, FileCheck2, ShieldCheck, Landmark, Check,
} from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";
import { openBooking } from "@/lib/booking";

const API = process.env.REACT_APP_BACKEND_URL;
const PATASHALA_OPTIONS = [
  "Sri Sharada Peetham, Sringeri",
  "Sri Dharmasthala Manjunatheshwara Veda Patashala",
  "Udupi Ashta Mathas",
  "Other matha / gurukula",
];
const VEDA_OPTIONS = ["Rig Veda", "Yajur Veda", "Sama Veda", "Atharva Veda", "Agama / Other"];
const YEAR_OPTIONS = ["2026", "2025", "2024", "2023", "2022", "2021", "2020", "Earlier"];

const DOC_FIELDS = [
  { key: "certificate", kind: "completion_certificate", label: "Final-year completion certificate", kn: "ಪೂರ್ಣಗೊಳಿಸಿದ ಪ್ರಮಾಣಪತ್ರ" },
  { key: "id", kind: "photo_id", label: "Photo ID (Aadhaar / PAN / Voter ID)", kn: "ಫೋಟೋ ಗುರುತಿನ ಚೀಟಿ" },
  { key: "photo", kind: "passport_photo", label: "Passport-size photograph", kn: "ಪಾಸ್‌ಪೋರ್ಟ್ ಅಳತೆಯ ಫೋಟೋ" },
];
const LBL = "block font-mukta text-sm font-semibold text-[#3B221B]";
const INP = "mt-1 w-full rounded-[12px] border border-[#A8722A]/30 bg-[#FBF6EC] px-4 py-2.5 font-mukta text-sm text-[#3B221B] outline-none transition-colors focus:border-[#B23A2B] focus:ring-2 focus:ring-[#B23A2B]/20";

const IMG = {
  hero: "https://images.unsplash.com/photo-1686624386665-4cd01b96d0f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHx2ZWRpYyUyMGd1cnVrdWxhJTIwc3R1ZGVudHMlMjBsZWFybmluZ3xlbnwwfHx8fDE3OTE1NjgzMTh8MA&ixlib=rb-4.1.0&q=85",
  aarti: "https://images.unsplash.com/photo-1763610651463-4e7b3befbcc8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxoaW5kdSUyMHByaWVzdCUyMHBlcmZvcm1pbmclMjBob21hJTIwZmlyZSUyMHJpdHVhbHxlbnwwfHx8fDE3OTE1NjgzMTh8MA&ixlib=rb-4.1.0&q=85",
  homa: "https://images.unsplash.com/photo-1630764883473-e8c2056f0589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxoaW5kdSUyMHByaWVzdCUyMHBlcmZvcm1pbmclMjBob21hJTIwZmlyZSUyMHJpdHVhbHxlbnwwfHx8fDE3OTE1NjgzMTh8MA&ixlib=rb-4.1.0&q=85",
  youngPriest: "https://images.unsplash.com/photo-1647025087950-d3e16988c2e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHx5b3VuZyUyMGhpbmR1JTIwcHJpZXN0JTIwdGVtcGxlJTIwaW5kaWF8ZW58MHx8fHwxNzkxNTY4MzE4fDA&ixlib=rb-4.1.0&q=85",
  templePriests: "https://images.unsplash.com/photo-1651471239789-029204bf7124?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHx5b3VuZyUyMGhpbmR1JTIwcHJpZXN0JTIwdGVtcGxlJTIwaW5kaWF8ZW58MHx8fHwxNzkxNTY4MzE4fDA&ixlib=rb-4.1.0&q=85",
};

const CDN = "https://customer-assets-7cd3h4nn.emergentagent.net/job_pooja-marketplace-3/artifacts";
const HERITAGE = {
  sringeri: `${CDN}/bda57ab2672a6f60_Vedic%20Learning%20at%20Sringeri%20Sharada%20Peetham.png`,
  collage: `${CDN}/42b8cd9435747fd7_Sringeri%20Veda%20Pathashala_%20Preserving%20Wisdom.png`,
  udupi: `${CDN}/8be7d2d039e63d75_Udupi%20Sri%20Krishna%20Matha_%20Vedic%20Learning.png`,
  dharmasthala: `${CDN}/4292042cf6d5e651_Siddhavana%20Gurukula_%20Tradition%20and%20Learning.png`,
  acharya: `${CDN}/6b0b0bd2aaa6dce0_Vedic%20Lessons%20in%20a%20Sunlit%20Temple%20Courtyard.png`,
};
const NETWORK = [
  { name: "Udupi & its Maths", img: HERITAGE.udupi, d: "Preserve the rich Madhwa philosophical tradition and support traditional scholarship and religious learning." },
  { name: "Dharmasthala", img: HERITAGE.dharmasthala, d: "A longstanding tradition of devotion, service and education, alongside its broader cultural and educational institutions." },
];
const NETWORK_TEXT = [
  { name: "Poornaprajna Vidyapeetha, Bengaluru", d: "An established centre for Sanskrit, Vedic studies and traditional philosophical education." },
  { name: "Other Veda Pathashalas & Gurukulas", d: "Traditional learning centres across Karnataka preserving sacred knowledge and cultural continuity." },
];

const BENEFITS = [
  { Icon: CalendarClock, kn: "ನಿರಂತರ ಬುಕಿಂಗ್", en: "Steady bookings near you", d: "Families in your area request poojas, homas and samskaras — work close to home, around your schedule." },
  { Icon: IndianRupee, kn: "ನ್ಯಾಯಯುತ ದಕ್ಷಿಣೆ", en: "Fair, transparent dakshina", d: "Agreed amounts shown upfront, with respect. No bargaining, no middlemen taking a cut." },
  { Icon: BadgeCheck, kn: "ಪರಿಶೀಲಿತ ಗುರುತು", en: "Verified profile & recognition", d: "A KYC-verified profile that showcases your patashala, your shakha and your expertise to every family." },
  { Icon: BookOpen, kn: "ನಿಮ್ಮ ಸಂಪ್ರದಾಯ", en: "Your shakha, your tradition", d: "You choose exactly what you perform and in which sampradaya — Smarta, Madhwa or Srivaishnava." },
  { Icon: Users, kn: "ಹಿರಿಯ ವೈದಿಕರ ಮಾರ್ಗದರ್ಶನ", en: "Mentorship from senior vaidikas", d: "Learn the practical side of karmakanda from experienced acharyas already on the platform." },
  { Icon: Sparkles, kn: "ಕಲಿಯುತ್ತಾ ಗಳಿಸಿ", en: "Study now, earn alongside", d: "Senior students can take on suitable ceremonies while completing their adhyayana." },
];

const PATASHALAS = [
  { kn: "ಶ್ರೀ ಶಾರದಾ ಪೀಠ, ಶೃಂಗೇರಿ", en: "Sri Sharada Peetham, Sringeri" },
  { kn: "ಶ್ರೀ ಧರ್ಮಸ್ಥಳ ಮಂಜುನಾಥೇಶ್ವರ ವೇದ ಪಾಠಶಾಲಾ", en: "Sri Dharmasthala Manjunatheshwara Veda Patashala" },
  { kn: "ಉಡುಪಿ ಅಷ್ಟ ಮಠಗಳು", en: "Udupi Ashta Mathas" },
  { kn: "ಕರ್ನಾಟಕದ ಇತರ ಮಠ-ಗುರುಕುಲಗಳು", en: "Other mathas & gurukulas across Karnataka" },
];

const STEPS = [
  { n: "1", kn: "ಅಂತಿಮ ವರ್ಷ ಪೂರ್ಣಗೊಳಿಸಿ", en: "Complete your final year", d: "Finish your adhyayana at a recognised Veda patashala, matha or gurukula." },
  { n: "2", kn: "ದಾಖಲೆಗಳೊಂದಿಗೆ ನೋಂದಣಿ", en: "Enroll with your documents", d: "Upload your completion certificate, photo ID and a passport photograph — it takes two minutes." },
  { n: "3", kn: "ಪರಿಶೀಲನೆ", en: "Get verified", d: "Our team reviews your documents and approves your purohith profile." },
  { n: "4", kn: "ಬುಕಿಂಗ್ ಸ್ವೀಕರಿಸಿ", en: "Receive bookings", d: "Families near you request your services; grow with reviews and mentorship." },
];

const FAQS = [
  { q: "Who can enroll on PurohithConnect as a student?", a: "Senior students and recent graduates of recognised Veda and Agama patashalas — including Sringeri, Dharmasthala, Udupi and other mathas and gurukulas across Karnataka." },
  { q: "Is there any fee for students to join?", a: "No. Enrollment for patashala students and graduates will always be free. PurohithConnect only exists to bring you dignified work." },
  { q: "Do I need to have finished my studies?", a: "Not necessarily. Senior students in their final years and recent graduates are both welcome. You take on only the ceremonies you are ready for." },
  { q: "Will my tradition and shakha be respected?", a: "Completely. You choose exactly which ceremonies you perform and in which sampradaya. Families are matched to you based on their tradition and yours." },
  { q: "Which areas can I serve?", a: "We are starting in Bengaluru and expanding across Karnataka. You set the areas you are willing to travel to." },
];

const Eyebrow = ({ children }) => (
  <p className="font-baloo text-base text-[#B23A2B]">{children}</p>
);

const FileField = ({ field, file, onPick, testid }) => {
  const ref = useRef(null);
  return (
    <div>
      <p className="font-mukta text-sm font-semibold text-[#3B221B]">{field.label} <span className="text-[#B23A2B]">*</span></p>
      <p className="font-baloo text-xs text-[#A8722A]">{field.kn}</p>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden" data-testid={testid}
        onChange={(e) => onPick(e.target.files?.[0] || null)} />
      <button type="button" onClick={() => ref.current?.click()}
        className={`mt-2 flex w-full items-center gap-3 rounded-[12px] border-2 border-dashed px-4 py-3 text-left transition-colors ${file ? "border-[#3E6B4F]/50 bg-[#3E6B4F]/5" : "border-[#A8722A]/40 bg-[#FFFDF8] hover:border-[#B23A2B]/50"}`}>
        {file ? <FileCheck2 size={20} className="shrink-0 text-[#3E6B4F]" /> : <Upload size={20} className="shrink-0 text-[#A8722A]" />}
        <span className="min-w-0 flex-1 truncate font-mukta text-sm text-[#6B5044]">{file ? file.name : "Tap to upload (JPG, PNG or PDF · max 10 MB)"}</span>
      </button>
    </div>
  );
};

export default function PatashalaPage() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", patashala: "", patashala_other: "", veda: "", shakha: "", year_completed: "", area: "", note: "" });
  const [files, setFiles] = useState({ certificate: null, id: null, photo: null });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const scrollToEnroll = () => document.getElementById("enroll")?.scrollIntoView({ behavior: "smooth", block: "start" });

  const uploadOne = async (kind, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API}/api/patashala/upload?kind=${kind}`, { method: "POST", body: fd });
    if (!res.ok) { const er = await res.json().catch(() => ({})); throw new Error(er.detail || "Upload failed"); }
    return (await res.json()).id;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.patashala || !form.year_completed) {
      toast.error("Please fill in your name, phone, patashala and year of completion"); return;
    }
    if (form.patashala === "Other matha / gurukula" && !form.patashala_other.trim()) {
      toast.error("Please enter your matha / gurukula name"); return;
    }
    if (!files.certificate || !files.id || !files.photo) {
      toast.error("Please upload all three required documents"); return;
    }
    setSubmitting(true);
    try {
      const certId = await uploadOne("completion_certificate", files.certificate);
      const idId = await uploadOne("photo_id", files.id);
      const photoId = await uploadOne("passport_photo", files.photo);
      const res = await fetch(`${API}/api/patashala/enroll`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, certificate_file_id: certId, id_file_id: idId, photo_file_id: photoId }),
      });
      if (!res.ok) { const er = await res.json().catch(() => ({})); throw new Error(er.detail || "Submission failed"); }
      setDone(true);
      toast.success("Enrollment submitted — we'll be in touch soon!");
      setTimeout(() => document.getElementById("enroll")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (err) {
      toast.error(err.message || "Something went wrong, please try again");
    } finally {
      setSubmitting(false);
    }
  };

  useSeo({
    title: "Patashala Students — ಪಾಠಶಾಲಾ ವಿದ್ಯಾರ್ಥಿಗಳಿಗೆ ಉದ್ಯೋಗ | Vedic Student Employment | PurohithConnect",
    description: "PurohithConnect is the employment platform for Veda patashala students & graduates from Sringeri, Dharmasthala, Udupi and mathas across Karnataka. Steady bookings, fair dakshina, verified profiles — your Vedic learning, a dignified livelihood.",
    canonicalPath: "/patashala",
    jsonLd: useMemo(() => ([
      {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: FAQS.map((x) => ({ "@type": "Question", name: x.q, acceptedAnswer: { "@type": "Answer", text: x.a } })),
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://purohithconnect.com/" },
          { "@type": "ListItem", position: 2, name: "Patashala", item: "https://purohithconnect.com/patashala" },
        ],
      },
    ]), []),
  });

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
      <SiteHeader openWaitlist={() => openBooking({ location: "patashala-header" })} />

      <main>
        {/* hero */}
        <section className="relative overflow-hidden bg-[#2A0E0A]" data-testid="patashala-hero">
          {/* gurukula image mosaic */}
          <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-1.5 md:grid-cols-4" aria-hidden="true">
            <img src={HERITAGE.sringeri} alt="" className="col-span-1 row-span-2 h-full w-full object-cover" />
            <img src={HERITAGE.acharya} alt="" className="h-full w-full object-cover" />
            <img src={HERITAGE.collage} alt="" className="hidden h-full w-full object-cover md:block" />
            <img src={HERITAGE.udupi} alt="" className="h-full w-full object-cover" />
            <img src={IMG.aarti} alt="" className="hidden h-full w-full object-cover md:block" />
            <img src={HERITAGE.dharmasthala} alt="" className="hidden h-full w-full object-cover md:block" />
            <img src={IMG.homa} alt="" className="hidden h-full w-full object-cover md:block" />
          </div>
          {/* warm wash + readability gradient */}
          <div className="absolute inset-0 bg-[#B23A2B]/15 mix-blend-multiply" aria-hidden="true" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1F0906] via-[#2A0E0A]/90 to-[#2A0E0A]/45" aria-hidden="true" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1F0906] via-transparent to-[#1F0906]/30" aria-hidden="true" />

          <div className="relative mx-auto flex min-h-[80vh] max-w-7xl flex-col justify-center px-4 py-20 md:px-8 md:py-28">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#F0C98A]/40 bg-[#2A0E0A]/50 px-4 py-1.5 font-baloo text-sm text-[#F0C98A] backdrop-blur-sm">
                <Flame size={14} /> ಗುರುಕುಲ ಪರಂಪರೆ · A living gurukula tradition
              </span>
              <p className="mt-4 font-baloo text-lg text-[#F0C98A]">ಪಾಠಶಾಲಾ ವಿದ್ಯಾರ್ಥಿಗಳಿಗೆ · ಅಂತಿಮ ವರ್ಷ ಪೂರ್ಣಗೊಳಿಸಿದವರಿಗೆ</p>
              <h1 className="mt-2 font-eczar text-4xl font-bold leading-tight text-[#FFFDF8] drop-shadow-[0_2px_12px_rgba(0,0,0,0.55)] sm:text-5xl lg:text-6xl" data-testid="patashala-title">
                From the patashala to the puja
              </h1>
              <p className="mt-4 font-mukta text-base text-[#F3E3C9] md:text-lg">
                Completed your final year at a Veda patashala? Your years of study deserve a dignified livelihood.
                PurohithConnect connects graduates of patashalas — Sringeri, Dharmasthala, Udupi and mathas across Karnataka —
                with families who need authentic, tradition-true rituals. <span className="font-semibold text-[#FFFDF8]">Enroll in minutes by uploading your documents.</span>
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                <button onClick={scrollToEnroll}
                  data-testid="patashala-hero-enroll"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C99B55] px-7 py-3 font-mukta text-base font-semibold text-[#2A0E0A] shadow-lg transition-transform hover:-translate-y-0.5 hover:bg-[#d8ac67]">
                  <GraduationCap size={18} /> Enroll now
                </button>
                <span className="inline-flex items-center gap-2 rounded-full border border-[#F0C98A]/40 px-4 py-2 font-mukta text-sm text-[#F0C98A]">
                  <ShieldCheck size={15} /> Open for final-year graduates
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* mission */}
        <section className="mx-auto max-w-5xl px-4 py-16 text-center md:px-8" data-testid="patashala-mission">
          <Eyebrow>ನಮ್ಮ ಸಂಕಲ್ಪ</Eyebrow>
          <h2 className="mt-2 font-eczar text-3xl font-bold md:text-4xl">The reality we want to change</h2>
          <p className="mx-auto mt-4 max-w-3xl font-mukta text-base text-[#6B5044] md:text-lg">
            Every year, thousands of students complete years of rigorous study in Veda, Agama and karmakanda — yet many
            struggle to find steady, respectful work near home. Families, meanwhile, search anxiously for a verified purohith
            who honours their shakha and performs each ritual the right way. <span className="font-semibold text-[#3B221B]">PurohithConnect closes that gap</span> — turning
            deep traditional learning into a dependable livelihood, and keeping our paramparā alive for the next generation.
          </p>
        </section>

        {/* benefits */}
        <section className="mx-auto max-w-7xl px-4 pb-6 md:px-8" data-testid="patashala-benefits">
          <div className="text-center">
            <Eyebrow>ಏಕೆ PurohithConnect?</Eyebrow>
            <h2 className="mt-2 font-eczar text-2xl font-semibold md:text-3xl">What enrolling gives a young purohith</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b) => (
              <article key={b.en} data-testid={`patashala-benefit-${b.en.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group rounded-[18px] border border-[#A8722A]/25 bg-[#FFFDF8] p-6 shadow-sm transition-shadow hover:shadow-md">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F3EADA] text-[#B23A2B] transition-colors group-hover:bg-[#B23A2B] group-hover:text-[#FFFDF8]">
                  <b.Icon size={24} />
                </div>
                <p className="mt-4 font-baloo text-base text-[#C99B55]">{b.kn}</p>
                <h3 className="font-eczar text-lg font-semibold text-[#3B221B]">{b.en}</h3>
                <p className="mt-2 font-mukta text-sm text-[#6B5044]">{b.d}</p>
              </article>
            ))}
          </div>
        </section>

        {/* image quote band */}
        <section className="relative my-16 overflow-hidden" data-testid="patashala-quote-band">
          <div className="absolute inset-0">
            <img src={HERITAGE.acharya} alt="An Acharya teaching Vedic students in a gurukula courtyard" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-[#2A0E0A]/78" />
          </div>
          <div className="relative mx-auto max-w-4xl px-4 py-20 text-center md:px-8">
            <Flame size={30} className="mx-auto text-[#F0C98A]" />
            <p className="mt-4 font-baloo text-2xl text-[#F0C98A] md:text-3xl">ವಿದ್ಯೆಗೆ ಗೌರವ · ಗುರುಕುಲಕ್ಕೆ ಜೀವನೋಪಾಯ</p>
            <p className="mt-3 font-eczar text-xl font-semibold text-[#FFFDF8] md:text-2xl">
              Honour for learning. A livelihood for the gurukula.
            </p>
          </div>
        </section>

        {/* who it's for / patashalas */}
        <section className="mx-auto max-w-7xl px-4 md:px-8" data-testid="patashala-partners">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <Eyebrow>ಯಾರಿಗಾಗಿ?</Eyebrow>
              <h2 className="mt-2 font-eczar text-2xl font-semibold md:text-3xl">The patashalas & mathas we're reaching out to</h2>
              <p className="mt-3 font-mukta text-base text-[#6B5044]">
                Whether you study Rig, Yajur, Sama or Atharva veda — Smarta, Madhwa or Srivaishnava sampradaya — there is a
                place for you here.
              </p>
              <ul className="mt-6 space-y-3">
                {PATASHALAS.map((p) => (
                  <li key={p.en} data-testid={`patashala-item-${p.en.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="flex items-start gap-3 rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-4 shadow-sm">
                    <GraduationCap size={22} className="mt-0.5 shrink-0 text-[#B23A2B]" />
                    <div>
                      <p className="font-eczar text-base font-semibold text-[#3B221B]">{p.en}</p>
                      <p className="font-baloo text-sm text-[#A8722A]">{p.kn}</p>
                      {p.en.includes("Sringeri") && (
                        <Link to="/patashala/sringeri" data-testid="patashala-sringeri-link"
                          className="mt-1 inline-flex items-center gap-1 font-mukta text-xs font-semibold text-[#B23A2B] hover:text-[#8f1623]">
                          Read our Sringeri heritage story <ArrowRight size={13} />
                        </Link>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src={HERITAGE.sringeri} alt="Sri Sharada Peetham, Sringeri — Veda Pathashala" loading="lazy" className="h-48 w-full rounded-[18px] object-cover shadow-md" />
              <img src={HERITAGE.dharmasthala} alt="Sri Dharmasthala Manjunatheshwara Veda Patashala" loading="lazy" className="h-48 w-full rounded-[18px] object-cover shadow-md" />
              <img src={HERITAGE.udupi} alt="Udupi Ashta Mathas — Vedic learning" loading="lazy" className="h-48 w-full rounded-[18px] object-cover shadow-md" />
              <img src={HERITAGE.acharya} alt="An Acharya teaching Vedic students in a gurukula" loading="lazy" className="h-48 w-full rounded-[18px] object-cover shadow-md" />
            </div>
          </div>
        </section>

        {/* vedic heritage */}
        <section className="bg-[#2A0E0A] py-16 text-[#F3E3C9]" data-testid="patashala-heritage">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="grid items-center gap-10 lg:grid-cols-2">
              <img src={HERITAGE.collage} alt="Sri Sharada Peetham, Sringeri — Veda Pathashala life" loading="lazy" className="w-full rounded-[20px] shadow-lg" />
              <div>
                <p className="font-baloo text-base text-[#F0C98A]">ಪರಂಪರೆಯ ಗೌರವ</p>
                <h2 className="mt-1 font-eczar text-3xl font-bold text-[#FFFDF8] md:text-4xl">Preserving the eternal wisdom of the Vedas</h2>
                <div className="mt-4 space-y-4 font-mukta text-base leading-relaxed text-[#E8D5BC]">
                  <p>The Veda Pathashala tradition is a living heritage, where students learn under the guidance of their Acharyas — preserving the discipline, recitation and knowledge transmitted through the Guru–Shishya Parampara.</p>
                  <p>Vedic education is not merely the memorisation of sacred texts. It involves disciplined study, accurate recitation, guidance from experienced teachers and an understanding of the traditions within which the knowledge is practised. At PurohithConnect, we recognise that the future of our traditions depends on the people who dedicate themselves to learning, practising and passing on this sacred knowledge.</p>
                </div>
              </div>
            </div>

            {/* dedicated Sringeri card */}
            <Link to="/patashala/sringeri" data-testid="patashala-sringeri-card"
              className="group mt-10 flex flex-col overflow-hidden rounded-[20px] border border-[#F0C98A]/25 bg-[#3B140C] md:flex-row">
              <img src={HERITAGE.sringeri} alt="Sri Sharada Peetham, Sringeri Veda Patashala" loading="lazy" className="h-56 w-full object-cover md:h-auto md:w-2/5" />
              <div className="flex flex-col justify-center p-6 md:p-8">
                <p className="font-baloo text-base text-[#F0C98A]">ಶ್ರೀ ಶಾರದಾ ಪೀಠ, ಶೃಂಗೇರಿ</p>
                <h3 className="mt-1 font-eczar text-2xl font-bold text-[#FFFDF8]">Sri Sharada Peetham, Sringeri — Veda Patashala</h3>
                <p className="mt-2 font-mukta text-sm leading-relaxed text-[#E8D5BC]">On the banks of the sacred Tunga, associated with Jagadguru Adi Shankaracharya — a centre of Advaita Vedanta and Vedic learning for generations. Read our dedicated heritage story.</p>
                <span className="mt-4 inline-flex items-center gap-2 font-mukta text-sm font-semibold text-[#F0C98A]">
                  Visit the Sringeri Patashala page <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>

            {/* wider network */}
            <h3 className="mt-12 font-eczar text-2xl font-semibold text-[#FFFDF8]">From Sringeri to Karnataka: a wider network</h3>
            <p className="mt-2 max-w-3xl font-mukta text-sm leading-relaxed text-[#E8D5BC]">While Sringeri remains a central point of reference, Karnataka is home to several other important centres of traditional learning.</p>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {NETWORK.map((n) => (
                <article key={n.name} className="overflow-hidden rounded-[18px] border border-[#F0C98A]/20 bg-[#3B140C]" data-testid={`heritage-net-${n.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <img src={n.img} alt={n.name} loading="lazy" className="h-52 w-full object-cover" />
                  <div className="p-5">
                    <h4 className="font-eczar text-xl font-semibold text-[#FFFDF8]">{n.name}</h4>
                    <p className="mt-2 font-mukta text-sm leading-relaxed text-[#E8D5BC]">{n.d}</p>
                  </div>
                </article>
              ))}
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {NETWORK_TEXT.map((n) => (
                <article key={n.name} className="flex gap-3 rounded-[18px] border border-[#F0C98A]/20 bg-[#3B140C] p-5">
                  <Landmark size={22} className="mt-0.5 shrink-0 text-[#F0C98A]" />
                  <div>
                    <h4 className="font-eczar text-lg font-semibold text-[#FFFDF8]">{n.name}</h4>
                    <p className="mt-1 font-mukta text-sm leading-relaxed text-[#E8D5BC]">{n.d}</p>
                  </div>
                </article>
              ))}
            </div>

            {/* guiding belief */}
            <div className="mt-12 rounded-[20px] border border-[#F0C98A]/20 bg-[#8B1A2B] p-8 text-center md:p-10">
              <Flame size={28} className="mx-auto text-[#F0C98A]" />
              <p className="mt-3 font-eczar text-2xl font-bold leading-snug text-[#FFFDF8] md:text-3xl">Technology should not replace tradition. It should help people connect with it.</p>
              <p className="mt-4 font-eczar text-base font-semibold text-[#F0C98A]">PurohithConnect — Connecting Devotees with Tradition. Preserving Heritage for Generations.</p>
            </div>
            <p className="mt-6 text-center font-mukta text-xs leading-relaxed text-[#C9B7A3]">This heritage initiative does not imply an official association, endorsement or integration with Sri Sharada Peetham, Sringeri, or any other institution unless formally confirmed.</p>
          </div>
        </section>

        {/* how it works */}
        <section className="mx-auto max-w-7xl px-4 py-16 md:px-8" data-testid="patashala-how">
          <div className="text-center">
            <Eyebrow>ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ</Eyebrow>
            <h2 className="mt-2 font-eczar text-2xl font-semibold md:text-3xl">How enrollment will work</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <div key={s.n} data-testid={`patashala-step-${s.n}`} className="relative rounded-[18px] border border-[#A8722A]/25 bg-[#FFFDF8] p-6 shadow-sm">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#B23A2B] font-eczar text-lg font-bold text-[#FFFDF8]">{s.n}</span>
                <p className="mt-4 font-baloo text-base text-[#C99B55]">{s.kn}</p>
                <h3 className="font-eczar text-lg font-semibold text-[#3B221B]">{s.en}</h3>
                <p className="mt-2 font-mukta text-sm text-[#6B5044]">{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* enrollment form */}
        <section id="enroll" className="scroll-mt-24 bg-[#F3EADA]/50 py-16" data-testid="patashala-enroll">
          <div className="mx-auto max-w-3xl px-4 md:px-8">
            <div className="text-center">
              <Eyebrow>ನೋಂದಣಿ</Eyebrow>
              <h2 className="mt-2 font-eczar text-3xl font-bold md:text-4xl">Enroll as a purohith</h2>
              <p className="mx-auto mt-3 max-w-xl font-mukta text-base text-[#6B5044]">
                For students who have <span className="font-semibold text-[#3B221B]">completed their final year</span> at a Veda
                patashala or matha. Fill in your details and upload the three required documents — our team will verify and reach out.
              </p>
            </div>

            {done ? (
              <div className="mt-8 rounded-[20px] border border-[#3E6B4F]/30 bg-[#FFFDF8] p-8 text-center shadow-sm" data-testid="patashala-enroll-success">
                <CheckCircle2 size={46} className="mx-auto text-[#3E6B4F]" />
                <h3 className="mt-3 font-eczar text-2xl font-semibold text-[#3B221B]">ಧನ್ಯವಾದಗಳು! Enrollment received</h3>
                <p className="mx-auto mt-2 max-w-md font-mukta text-base text-[#6B5044]">
                  Thank you, {form.name.split(" ")[0] || "vidyarthi"}. We've received your documents and will verify your details and contact you on {form.phone}.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="mt-8 space-y-5 rounded-[20px] border border-[#A8722A]/25 bg-[#FFFDF8] p-6 shadow-sm md:p-8" data-testid="patashala-enroll-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className={LBL}>Full name <span className="text-[#B23A2B]">*</span></span>
                    <input className={INP} value={form.name} onChange={set("name")} placeholder="e.g. Ved. Sharma" data-testid="enroll-name" />
                  </label>
                  <label className="block">
                    <span className={LBL}>Phone <span className="text-[#B23A2B]">*</span></span>
                    <input className={INP} type="tel" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" data-testid="enroll-phone" />
                  </label>
                  <label className="block">
                    <span className={LBL}>Email</span>
                    <input className={INP} type="email" value={form.email} onChange={set("email")} placeholder="optional" data-testid="enroll-email" />
                  </label>
                  <label className="block">
                    <span className={LBL}>Area / City</span>
                    <input className={INP} value={form.area} onChange={set("area")} placeholder="e.g. Jayanagar, Bengaluru" data-testid="enroll-area" />
                  </label>
                  <label className="block">
                    <span className={LBL}>Patashala / Matha <span className="text-[#B23A2B]">*</span></span>
                    <select className={INP} value={form.patashala} onChange={set("patashala")} data-testid="enroll-patashala">
                      <option value="">Select…</option>
                      {PATASHALA_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className={LBL}>Year of completion <span className="text-[#B23A2B]">*</span></span>
                    <select className={INP} value={form.year_completed} onChange={set("year_completed")} data-testid="enroll-year">
                      <option value="">Select…</option>
                      {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </label>
                  {form.patashala === "Other matha / gurukula" && (
                    <label className="block sm:col-span-2">
                      <span className={LBL}>Name of your matha / gurukula <span className="text-[#B23A2B]">*</span></span>
                      <input className={INP} value={form.patashala_other} onChange={set("patashala_other")} data-testid="enroll-patashala-other" />
                    </label>
                  )}
                  <label className="block">
                    <span className={LBL}>Veda</span>
                    <select className={INP} value={form.veda} onChange={set("veda")} data-testid="enroll-veda">
                      <option value="">Select…</option>
                      {VEDA_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className={LBL}>Shakha / specialization</span>
                    <input className={INP} value={form.shakha} onChange={set("shakha")} placeholder="e.g. Taittiriya, Smarta" data-testid="enroll-shakha" />
                  </label>
                </div>

                <label className="block">
                  <span className={LBL}>Anything you'd like us to know</span>
                  <textarea className={INP} rows={3} value={form.note} onChange={set("note")} placeholder="Ceremonies you perform, languages, availability…" data-testid="enroll-note" />
                </label>

                <div>
                  <p className="font-mukta text-sm font-semibold uppercase tracking-wider text-[#A8722A]">Required documents</p>
                  <div className="mt-3 grid gap-4 sm:grid-cols-3">
                    {DOC_FIELDS.map((f) => (
                      <FileField key={f.key} field={f} file={files[f.key]} testid={`enroll-file-${f.key}`}
                        onPick={(file) => setFiles((s) => ({ ...s, [f.key]: file }))} />
                    ))}
                  </div>
                </div>

                <p className="font-mukta text-xs text-[#8A7B6F]">
                  By enrolling you confirm the documents are genuine and consent to PurohithConnect verifying them. Your documents are stored securely and used only for verification.
                </p>

                <button type="submit" disabled={submitting} data-testid="enroll-submit"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#B23A2B] px-7 py-3.5 font-mukta text-base font-semibold text-[#FFFDF8] transition-colors hover:bg-[#9a2f22] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto">
                  {submitting ? <><Loader2 size={18} className="animate-spin" /> Submitting…</> : <><GraduationCap size={18} /> Submit enrollment</>}
                </button>
              </form>
            )}
          </div>
        </section>

        {/* enrollment CTA */}
        <section className="mx-auto max-w-7xl px-4 pb-16 md:px-8" data-testid="patashala-cta">
          <div className="grid items-center gap-8 rounded-[24px] border border-[#A8722A]/25 bg-gradient-to-br from-[#3B140C] to-[#6A2015] p-8 md:grid-cols-[1fr_auto] md:p-12">
            <div>
              <p className="font-baloo text-lg text-[#F0C98A]">ಇಂದೇ ನೋಂದಾಯಿಸಿ</p>
              <h2 className="mt-1 font-eczar text-2xl font-bold text-[#FFFDF8] md:text-3xl">Completed your final year? Enroll today</h2>
              <p className="mt-3 max-w-2xl font-mukta text-base text-[#F3E3C9]">
                Graduates of Veda patashalas — upload your documents and join the platform. Running a patashala or matha?
                Partner with us to place your students.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <button onClick={scrollToEnroll}
                data-testid="patashala-cta-student"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C99B55] px-7 py-3 font-mukta text-base font-semibold text-[#2A0E0A] transition-transform hover:-translate-y-0.5 hover:bg-[#d8ac67]">
                <GraduationCap size={18} /> Enroll now
              </button>
              <a href="mailto:partners@purohithconnect.com?subject=Patashala%20partnership"
                data-testid="patashala-cta-partner"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#F0C98A]/50 px-7 py-3 font-mukta text-base font-semibold text-[#F0C98A] transition-colors hover:bg-[#F0C98A]/10">
                <HeartHandshake size={18} /> Partner as a patashala
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-3xl px-4 pb-20 md:px-8" data-testid="patashala-faq">
          <div className="text-center">
            <Eyebrow>ಪ್ರಶ್ನೋತ್ತರ</Eyebrow>
            <h2 className="mt-1 font-eczar text-2xl font-semibold md:text-3xl">Students' questions, answered</h2>
          </div>
          <div className="mt-6 space-y-3">
            {FAQS.map((x, i) => (
              <details key={i} className="group rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-4 shadow-sm [&_summary::-webkit-details-marker]:hidden" data-testid={`patashala-faq-${i}`} {...(i === 0 ? { open: true } : {})}>
                <summary className="flex cursor-pointer items-center justify-between gap-3 font-eczar text-[15px] font-semibold text-[#3B221B]">
                  {x.q}
                  <ChevronDown size={18} className="shrink-0 text-[#A8722A] transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-2 font-mukta text-sm text-[#6B5044]">{x.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link to="/purohiths" data-testid="patashala-to-purohiths" className="inline-flex items-center gap-1.5 font-mukta font-semibold text-[#A8722A] hover:text-[#8f5f21]">For practising purohiths <ArrowRight size={16} /></Link>
            <Link to="/traditions" data-testid="patashala-to-traditions" className="inline-flex items-center gap-1.5 font-mukta font-semibold text-[#A8722A] hover:text-[#8f5f21]">Traditions we honour <ArrowRight size={16} /></Link>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
